import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  DndContext, closestCenter, PointerSensor, KeyboardSensor,
  useSensor, useSensors,
} from '@dnd-kit/core';
import {
  SortableContext, useSortable, verticalListSortingStrategy, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  BookOpen, Check, Plus, Trash2, Pencil, GripVertical, X, Save,
  ChevronDown, ChevronRight, Star,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Drag-and-drop contingency library picker.
 *
 * Two sortable layers:
 *  - Outer: categories, drag by the category header handle
 *  - Inner: items within each category, drag by the row handle
 *
 * Every item is editable + deletable. The order persists — on drag-end
 * we POST new `position`/`cat_position` values to the backend, so the
 * next time the dialog opens the library is in your order.
 *
 * Props:
 *  - open, onOpenChange (controlled)
 *  - initialChecked: Set<string> — which texts are pre-checked when the
 *    dialog opens (used to show which provisions are already on the estimate)
 *  - onApply(texts: string[]) — called when the user hits Apply
 */
export default function ContingencyLibraryDialog({ open, onOpenChange, initialChecked, onApply }) {
  const [items, setItems] = useState([]);
  const [checked, setChecked] = useState({});
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all');
  const [editingId, setEditingId] = useState(null);
  const [editingText, setEditingText] = useState('');
  const [newText, setNewText] = useState('');
  const [newCategory, setNewCategory] = useState('');
  const [showNewCategory, setShowNewCategory] = useState(false);
  const [newItemCategory, setNewItemCategory] = useState('custom');
  // Collapsed categories — a Set of category names currently collapsed
  const [collapsed, setCollapsed] = useState(() => new Set());

  const toggleCollapsed = useCallback((cat) => {
    setCollapsed(prev => {
      const n = new Set(prev);
      if (n.has(cat)) n.delete(cat); else n.add(cat);
      return n;
    });
  }, []);

  // Only initialize (fetch + pre-check) on the open transition. Parent
  // re-renders pass a fresh `new Set(...)` for initialChecked on every
  // render, so including it in deps would wipe the user's in-progress
  // selections whenever the estimate auto-saves.
  const initializedRef = useRef(false);
  useEffect(() => {
    if (!open) {
      initializedRef.current = false;
      return;
    }
    if (initializedRef.current) return;
    initializedRef.current = true;

    setLoading(true);
    fetch(`${API}/contingency-library`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : [])
      .then(data => {
        setItems(data);
        // Pre-check items whose text matches what was already on the estimate
        const preCheck = {};
        (initialChecked || new Set()).forEach(t => { preCheck[t] = true; });
        setChecked(preCheck);
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Group by category preserving the cat_position sort coming from the API.
  const grouped = useMemo(() => {
    const order = [];
    const byCat = {};
    items.forEach(it => {
      const cat = it.category || 'custom';
      if (!(cat in byCat)) {
        byCat[cat] = [];
        order.push(cat);
      }
      byCat[cat].push(it);
    });
    // Each category's items are already sorted by backend; just honor it.
    return { order, byCat };
  }, [items]);

  const visibleCats = useMemo(() => {
    if (filter === 'all') return grouped.order;
    return grouped.order.filter(c => c === filter);
  }, [grouped, filter]);

  const toggle = (text) => {
    setChecked(prev => ({ ...prev, [text]: !prev[text] }));
  };

  // Star a single item as a default provision — new estimates auto-include
  // all defaulted items.
  const toggleDefault = async (item) => {
    const next = !item.is_default;
    setItems(prev => prev.map(i => i.item_id === item.item_id ? { ...i, is_default: next } : i));
    const res = await fetch(`${API}/contingency-library/${item.item_id}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      credentials: 'include', body: JSON.stringify({ is_default: next }),
    });
    if (!res.ok) {
      setItems(prev => prev.map(i => i.item_id === item.item_id ? { ...i, is_default: !next } : i));
      toast.error('Failed to update default');
    } else {
      toast.success(next ? 'Added to defaults for new estimates' : 'Removed from defaults');
    }
  };

  const setSelectionAsDefaults = async () => {
    const texts = items.filter(i => checked[i.text]).map(i => i.text);
    const res = await fetch(`${API}/contingency-library/set-defaults`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      credentials: 'include', body: JSON.stringify({ texts }),
    });
    if (res.ok) {
      setItems(prev => prev.map(i => ({ ...i, is_default: texts.includes(i.text) })));
      toast.success(`${texts.length} provision${texts.length !== 1 ? 's' : ''} set as defaults for new estimates`);
    } else {
      toast.error('Failed to save defaults');
    }
  };

  // ── Drag handlers ────────────────────────────────────────────
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor),
  );

  // Only consider droppables of the SAME layer as the active drag item
  // (cat:* when dragging a category, itm:* when dragging an item). This
  // prevents the drag from appearing to "lose selection" when the cursor
  // passes over items of another category during a category-level drag.
  const collisionDetection = useCallback((args) => {
    const activeId = String(args.active?.id || '');
    const kind = activeId.split(':')[0];
    if (!kind) return closestCenter(args);
    const filtered = args.droppableContainers.filter(c =>
      String(c.id).startsWith(kind + ':')
    );
    return closestCenter({ ...args, droppableContainers: filtered });
  }, []);

  const persistOrder = async (updates) => {
    // Optimistic; the backend happily accepts the full list.
    try {
      await fetch(`${API}/contingency-library/reorder`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        credentials: 'include', body: JSON.stringify({ items: updates }),
      });
    } catch {
      toast.error('Reorder did not save');
    }
  };

  // We tag DnD ids as "cat:<name>" and "itm:<id>" so we can tell which
  // layer the drag came from.
  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const [aKind, aId] = String(active.id).split(':', 2);
    const [oKind, oId] = String(over.id).split(':', 2);
    if (aKind !== oKind) return;  // never mix layers

    if (aKind === 'cat') {
      // Reorder categories
      const oldIdx = grouped.order.indexOf(aId);
      const newIdx = grouped.order.indexOf(oId);
      if (oldIdx < 0 || newIdx < 0) return;
      const newOrder = arrayMove(grouped.order, oldIdx, newIdx);
      // Rebuild items with new cat_positions
      const updates = [];
      const nextItems = items.map(it => {
        const ci = newOrder.indexOf(it.category);
        const newCatPos = (ci + 1) * 10;
        if (it.cat_position !== newCatPos) {
          updates.push({ item_id: it.item_id, cat_position: newCatPos });
          return { ...it, cat_position: newCatPos };
        }
        return it;
      });
      setItems(nextItems.sort(
        (a, b) => (a.cat_position - b.cat_position) || (a.position - b.position)
      ));
      persistOrder(updates);
      return;
    }

    if (aKind === 'itm') {
      // Reorder items within a category
      const activeItem = items.find(i => i.item_id === aId);
      const overItem = items.find(i => i.item_id === oId);
      if (!activeItem || !overItem) return;
      if (activeItem.category !== overItem.category) return; // no cross-cat drag for now
      const catItems = grouped.byCat[activeItem.category] || [];
      const oldIdx = catItems.findIndex(i => i.item_id === aId);
      const newIdx = catItems.findIndex(i => i.item_id === oId);
      if (oldIdx < 0 || newIdx < 0) return;
      const reordered = arrayMove(catItems, oldIdx, newIdx);
      const updates = reordered.map((it, i) => ({
        item_id: it.item_id,
        position: (i + 1) * 10,
      }));
      // Apply to state
      const positionById = Object.fromEntries(updates.map(u => [u.item_id, u.position]));
      const nextItems = items
        .map(it => positionById[it.item_id]
          ? { ...it, position: positionById[it.item_id] }
          : it)
        .sort((a, b) => (a.cat_position - b.cat_position) || (a.position - b.position));
      setItems(nextItems);
      persistOrder(updates);
    }
  };

  // ── Edit / Delete / Add ──────────────────────────────────────
  const startEdit = (item) => {
    setEditingId(item.item_id);
    setEditingText(item.text);
  };

  const saveEdit = async () => {
    if (!editingId) return;
    const trimmed = editingText.trim();
    if (!trimmed) {
      toast.error('Text cannot be empty');
      return;
    }
    const res = await fetch(`${API}/contingency-library/${editingId}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      credentials: 'include', body: JSON.stringify({ text: trimmed }),
    });
    if (res.ok) {
      const updated = await res.json();
      setItems(prev => prev.map(i => i.item_id === editingId ? updated : i));
      // If this item was checked under the old text, migrate the check key.
      setChecked(prev => {
        const n = { ...prev };
        const oldItem = items.find(i => i.item_id === editingId);
        if (oldItem && n[oldItem.text]) {
          delete n[oldItem.text];
          n[trimmed] = true;
        }
        return n;
      });
      setEditingId(null);
      setEditingText('');
      toast.success('Updated');
    } else {
      toast.error('Failed to update');
    }
  };

  const cancelEdit = () => { setEditingId(null); setEditingText(''); };

  const deleteItem = async (item) => {
    if (!window.confirm(`Delete this ${item.is_preset ? 'preset' : ''} note?\n\n"${item.text.slice(0, 80)}${item.text.length > 80 ? '…' : ''}"`)) {
      return;
    }
    const res = await fetch(`${API}/contingency-library/${item.item_id}`, {
      method: 'DELETE', credentials: 'include',
    });
    if (res.ok) {
      setItems(prev => prev.filter(i => i.item_id !== item.item_id));
      setChecked(prev => {
        const n = { ...prev };
        delete n[item.text];
        return n;
      });
      toast.success('Deleted');
    } else {
      toast.error('Failed to delete');
    }
  };

  const addNewItem = async () => {
    const text = newText.trim();
    const category = (showNewCategory ? newCategory.trim() : newItemCategory) || 'custom';
    if (!text) {
      toast.error('Enter the provision text');
      return;
    }
    const res = await fetch(`${API}/contingency-library`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      credentials: 'include', body: JSON.stringify({ text, category }),
    });
    if (res.ok) {
      const created = await res.json();
      setItems(prev => [...prev, created].sort(
        (a, b) => (a.cat_position - b.cat_position) || (a.position - b.position)
      ));
      setChecked(prev => ({ ...prev, [created.text]: true }));
      setNewText('');
      setNewCategory('');
      setShowNewCategory(false);
      toast.success('Added to library');
    } else {
      toast.error('Failed to add');
    }
  };

  const apply = () => {
    const selected = Object.entries(checked).filter(([_, v]) => v).map(([t]) => t);
    // Preserve library order in the output — map back to item order.
    const orderedSelected = items
      .filter(i => checked[i.text])
      .map(i => i.text)
      // Dedupe (in case two items share text)
      .filter((t, idx, arr) => arr.indexOf(t) === idx);
    onApply(orderedSelected.length ? orderedSelected : selected);
  };

  // ── Render ───────────────────────────────────────────────────
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white rounded-md border border-[#E6EBE8] max-w-3xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: "'Chivo', sans-serif" }}>
            <BookOpen className="w-5 h-5 text-[#2D6B68]" /> Contingency Library
          </DialogTitle>
        </DialogHeader>

        {/* Category Filter Pills */}
        <div className="flex gap-2 flex-wrap items-center pb-3 border-b border-[#E6EBE8]">
          <button
            onClick={() => setFilter('all')}
            className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
              filter === 'all' ? 'bg-[#2D6B68] text-white' : 'bg-[#F0F3F1] text-[#6B7A73] hover:text-[#1A1D1C]'
            }`}
          >
            All
          </button>
          {grouped.order.map(cat => (
            <button
              key={cat}
              onClick={() => setFilter(cat)}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                filter === cat ? 'bg-[#2D6B68] text-white' : 'bg-[#F0F3F1] text-[#6B7A73] hover:text-[#1A1D1C]'
              }`}
            >
              {cat}
            </button>
          ))}
          <div className="ml-auto flex items-center gap-1">
            <button
              onClick={() => setCollapsed(new Set())}
              className="text-[10px] uppercase tracking-wider text-[#6B7A73] hover:text-[#2D6B68] px-2 py-1 rounded"
              title="Expand all categories"
              data-testid="expand-all-btn"
            >
              Expand all
            </button>
            <span className="text-[#C8D4CE]">·</span>
            <button
              onClick={() => setCollapsed(new Set(grouped.order))}
              className="text-[10px] uppercase tracking-wider text-[#6B7A73] hover:text-[#2D6B68] px-2 py-1 rounded"
              title="Collapse all categories"
              data-testid="collapse-all-btn"
            >
              Collapse all
            </button>
          </div>
        </div>

        {/* Sortable list */}
        <div className="flex-1 overflow-y-auto py-3 space-y-4" data-testid="library-list">
          {loading ? (
            <div className="flex items-center justify-center py-10">
              <div className="w-5 h-5 border-2 border-[#2D6B68] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <DndContext sensors={sensors} collisionDetection={collisionDetection} onDragEnd={handleDragEnd}>
              <SortableContext
                items={visibleCats.map(c => `cat:${c}`)}
                strategy={verticalListSortingStrategy}
              >
                {visibleCats.map(cat => (
                  <CategoryBlock
                    key={cat}
                    cat={cat}
                    items={grouped.byCat[cat] || []}
                    checked={checked}
                    editingId={editingId}
                    editingText={editingText}
                    isCollapsed={collapsed.has(cat)}
                    onToggleCollapsed={() => toggleCollapsed(cat)}
                    onToggle={toggle}
                    onToggleDefault={toggleDefault}
                    onStartEdit={startEdit}
                    onChangeEdit={setEditingText}
                    onSaveEdit={saveEdit}
                    onCancelEdit={cancelEdit}
                    onDelete={deleteItem}
                  />
                ))}
              </SortableContext>
            </DndContext>
          )}
          {!loading && visibleCats.length === 0 && (
            <p className="text-xs text-[#6B7A73] italic text-center py-8">No provisions yet. Add one below to get started.</p>
          )}
        </div>

        {/* Add new */}
        <div className="pt-3 border-t border-[#E6EBE8] space-y-2">
          <p className="text-xs text-[#6B7A73]">Add a custom provision to your library:</p>
          <div className="flex gap-2">
            {showNewCategory ? (
              <div className="flex gap-1 w-44">
                <Input
                  value={newCategory}
                  onChange={e => setNewCategory(e.target.value)}
                  placeholder="New category…"
                  className="flex-1 bg-white border-[#E6EBE8] rounded-md text-xs"
                  autoFocus
                />
                <button
                  onClick={() => { setShowNewCategory(false); setNewCategory(''); }}
                  className="text-[#6B7A73] hover:text-red-500 px-1"
                  title="Cancel"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ) : (
              <Select
                value={newItemCategory}
                onValueChange={v => {
                  if (v === '__new__') { setShowNewCategory(true); setNewItemCategory('custom'); }
                  else setNewItemCategory(v);
                }}
              >
                <SelectTrigger className="w-44 bg-white border-[#E6EBE8] rounded-md text-xs" data-testid="new-category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {grouped.order.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  {!grouped.order.includes('custom') && <SelectItem value="custom">custom</SelectItem>}
                  <SelectItem value="__new__">+ New category…</SelectItem>
                </SelectContent>
              </Select>
            )}
            <Input
              placeholder="Type a new provision…"
              value={newText}
              onChange={e => setNewText(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') addNewItem(); }}
              className="flex-1 bg-white border-[#E6EBE8] rounded-md"
              data-testid="new-provision-input"
            />
            <Button
              onClick={addNewItem}
              className="bg-[#2D6B68] text-white hover:bg-[#235552] rounded-md"
              data-testid="add-provision-btn"
            >
              <Plus className="w-4 h-4" />
            </Button>
          </div>
        </div>

        <DialogFooter className="gap-2 flex-row items-center flex-wrap">
          <span className="text-xs text-[#6B7A73] mr-auto">
            {Object.values(checked).filter(Boolean).length} selected
            <span className="text-[#C8D4CE]"> · </span>
            <Star className="w-3 h-3 inline -mt-0.5 text-amber-500 fill-amber-400" /> = auto-added to new estimates
          </span>
          <Button
            variant="outline"
            onClick={setSelectionAsDefaults}
            className="border-amber-300 text-amber-800 hover:bg-amber-50"
            title="New estimates will start with the currently checked provisions"
            data-testid="set-defaults-btn"
          >
            <Star className="w-3.5 h-3.5 mr-1.5" /> Set selection as defaults
          </Button>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-[#E6EBE8]">
            Cancel
          </Button>
          <Button onClick={apply} className="bg-[#2D6B68] text-white hover:bg-[#235552]" data-testid="apply-library-btn">
            Apply to Estimate
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Category block (outer sortable) ──────────────────────────────

function CategoryBlock({
  cat, items, checked, editingId, editingText,
  isCollapsed, onToggleCollapsed,
  onToggle, onToggleDefault, onStartEdit, onChangeEdit, onSaveEdit, onCancelEdit, onDelete,
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: `cat:${cat}` });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  const checkedCount = items.filter(i => checked[i.text]).length;
  return (
    <div ref={setNodeRef} style={style} data-testid={`cat-block-${cat}`}>
      <div className="flex items-center gap-2 mb-2">
        <button
          {...attributes} {...listeners}
          className="text-[#C8D4CE] hover:text-[#6B7A73] cursor-grab active:cursor-grabbing"
          title="Drag to reorder category"
          data-testid={`cat-drag-${cat}`}
        >
          <GripVertical className="w-3.5 h-3.5" />
        </button>
        <button
          onClick={onToggleCollapsed}
          className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] font-bold text-[#2D6B68] hover:text-[#1A4F4C] transition-colors group/head"
          title={isCollapsed ? 'Expand' : 'Collapse'}
          data-testid={`cat-toggle-${cat}`}
        >
          {isCollapsed
            ? <ChevronRight className="w-3.5 h-3.5" />
            : <ChevronDown className="w-3.5 h-3.5" />}
          <span>{cat}</span>
          <span className="text-[10px] font-normal text-[#6B7A73] normal-case tracking-normal">
            ({items.length}{checkedCount > 0 ? ` · ${checkedCount} selected` : ''})
          </span>
        </button>
      </div>
      {!isCollapsed && (
        <SortableContext
          items={items.map(i => `itm:${i.item_id}`)}
          strategy={verticalListSortingStrategy}
        >
          <div className="space-y-1">
            {items.map(item => (
              <LibraryRow
                key={item.item_id}
                item={item}
                isChecked={!!checked[item.text]}
                isEditing={editingId === item.item_id}
                editingText={editingText}
                onToggle={onToggle}
                onToggleDefault={onToggleDefault}
                onStartEdit={onStartEdit}
                onChangeEdit={onChangeEdit}
                onSaveEdit={onSaveEdit}
                onCancelEdit={onCancelEdit}
                onDelete={onDelete}
              />
            ))}
          </div>
        </SortableContext>
      )}
    </div>
  );
}

// ─── Library row (inner sortable) ─────────────────────────────────

function LibraryRow({
  item, isChecked, isEditing, editingText,
  onToggle, onToggleDefault, onStartEdit, onChangeEdit, onSaveEdit, onCancelEdit, onDelete,
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: `itm:${item.item_id}` });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-start gap-2 px-2 py-2 rounded-md group transition-colors ${
        isChecked ? 'bg-[rgba(45,107,104,0.08)]' : 'hover:bg-[#F9F9F8]'
      }`}
      data-testid={`library-item-${item.item_id}`}
    >
      <button
        {...attributes} {...listeners}
        className="text-[#C8D4CE] hover:text-[#6B7A73] cursor-grab active:cursor-grabbing mt-0.5"
        title="Drag to reorder"
      >
        <GripVertical className="w-3.5 h-3.5" />
      </button>
      {!isEditing && (
        <button
          onClick={() => onToggle(item.text)}
          className={`w-5 h-5 mt-0.5 rounded border-2 flex-shrink-0 flex items-center justify-center transition-colors ${
            isChecked ? 'bg-[#2D6B68] border-[#2D6B68]' : 'border-[#C8D4CE]'
          }`}
          data-testid={`toggle-item-${item.item_id}`}
        >
          {isChecked && <Check className="w-3 h-3 text-white" />}
        </button>
      )}
      {isEditing ? (
        <>
          <Input
            value={editingText}
            onChange={e => onChangeEdit(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') onSaveEdit();
              if (e.key === 'Escape') onCancelEdit();
            }}
            className="flex-1 bg-white border-[#E6EBE8] text-sm"
            autoFocus
            data-testid={`edit-input-${item.item_id}`}
          />
          <button
            onClick={onSaveEdit}
            className="p-1 text-emerald-600 hover:bg-emerald-50 rounded"
            title="Save"
            data-testid={`save-edit-${item.item_id}`}
          >
            <Save className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={onCancelEdit}
            className="p-1 text-[#6B7A73] hover:bg-[#F0F3F1] rounded"
            title="Cancel"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </>
      ) : (
        <>
          <span
            className="text-sm text-[#3A423E] leading-snug flex-1 cursor-pointer"
            onClick={() => onToggle(item.text)}
          >
            {item.text}
          </span>
          <button
            onClick={() => onToggleDefault(item)}
            className={`p-1 rounded hover:bg-white transition-opacity ${item.is_default ? 'text-amber-500' : 'text-[#6B7A73] hover:text-amber-500 opacity-0 group-hover:opacity-100'}`}
            title={item.is_default ? 'Default — auto-added to new estimates (click to remove)' : 'Make default for new estimates'}
            data-testid={`default-btn-${item.item_id}`}
          >
            <Star className={`w-3 h-3 ${item.is_default ? 'fill-amber-400' : ''}`} />
          </button>
          <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              onClick={() => onStartEdit(item)}
              className="p-1 text-[#6B7A73] hover:text-[#2D6B68] hover:bg-white rounded"
              title="Edit"
              data-testid={`edit-btn-${item.item_id}`}
            >
              <Pencil className="w-3 h-3" />
            </button>
            <button
              onClick={() => onDelete(item)}
              className="p-1 text-[#6B7A73] hover:text-red-600 hover:bg-white rounded"
              title="Delete"
              data-testid={`delete-btn-${item.item_id}`}
            >
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        </>
      )}
    </div>
  );
}
